import './icons/cup-icon';

class SlideToUnlock extends HTMLElement {
  private slider!: HTMLDivElement;
  private handle!: HTMLDivElement;
  private isDragging = false;
  private startX = 8;
  private currentX = 0;
  private maxX = 0;
  private unlocked = false;
  private prompt = 'Slide in front of the barista';
  disabled = false;

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }

  static get observedAttributes() {
    return ['prompt'];
  }

  attributeChangedCallback(name: string, _oldValue: string, newValue: string) {
    if (name === 'prompt') {
      this.prompt = newValue;
    }
  }

  connectedCallback() {
    this.render();
    this.slider = this.shadowRoot!.querySelector('.slider')!;
    this.handle = this.shadowRoot!.querySelector('.handle')!;
    this.maxX = this.slider.clientWidth - this.handle.clientWidth - 8;

    new ResizeObserver(() => {
      this.maxX = this.slider.clientWidth - this.handle.clientWidth - 8;
    }).observe(this.slider);

    this.handle.addEventListener('mousedown', this.startMouseDrag);
    this.handle.addEventListener('touchstart', this.startTouchDrag, { passive: false });
  }

  private startMouseDrag = (e: MouseEvent) => {
    if (this.unlocked || this.disabled) return;
    this.handle.style.transition = 'none';

    this.isDragging = true;
    this.startX = e.clientX - this.handle.offsetLeft;

    document.addEventListener('mousemove', this.onMouseDrag);
    document.addEventListener('mouseup', this.endMouseDrag);
  };

  private onMouseDrag = (e: MouseEvent) => {
    if (!this.isDragging) return;

    this.currentX = e.clientX - this.startX;
    this.updatePosition(this.currentX);
  };

  private endMouseDrag = () => {
    document.removeEventListener('mousemove', this.onMouseDrag);
    document.removeEventListener('mouseup', this.endMouseDrag);

    if (!this.isDragging) return;
    this.finishDrag();
  };

  private startTouchDrag = (e: TouchEvent) => {
    if (this.unlocked || this.disabled) return;
    this.handle.style.transition = 'none';

    this.isDragging = true;
    this.startX = e.touches[0].clientX - this.handle.offsetLeft;

    document.addEventListener('touchmove', this.onTouchDrag, { passive: false });
    document.addEventListener('touchend', this.endTouchDrag);
  };

  private onTouchDrag = (e: TouchEvent) => {
    if (!this.isDragging) return;

    e.preventDefault(); // prevent scrolling
    this.currentX = e.touches[0].clientX - this.startX;
    this.updatePosition(this.currentX);
  };

  private endTouchDrag = () => {
    document.removeEventListener('touchmove', this.onTouchDrag);
    document.removeEventListener('touchend', this.endTouchDrag);

    if (!this.isDragging) return;
    this.finishDrag();
  };

  private updatePosition(x: number) {
    const clampedX = Math.min(Math.max(0, x), this.maxX);
    this.handle.style.left = `${clampedX}px`;
  }

  private finishDrag() {
    this.isDragging = false;

    if (this.currentX >= this.maxX - 5) {
      this.unlock();
    } else {
      this.reset();
    }
  }

  private unlock() {
    this.unlocked = true;
    this.handle.style.transition = 'left 0.2s';
    this.handle.style.left = `${this.maxX}px`;
    this.dispatchEvent(new CustomEvent('unlocked'));
  }

  reset() {
    this.handle.style.left = '8px';
    this.handle.style.transition = 'left 0.2s';
    this.unlocked = false;
  }

  private render() {
    this.shadowRoot!.innerHTML = `
      <style>
        * {
            box-sizing: border-box;
            margin: 0;
            padding: 0;
        }
        .slider {
          width: 100%;
          height: 70px;
          background: black;
          border-radius: 16px;
          position: relative;
          user-select: none;
          touch-action: none;
          color: white;
            display: flex;
        }

        .handle {
          width: 54px;
          height: 54px;
          background: white;
          border-radius: 16px;
          position: absolute;
          top: 8px;
          left: 8px;
          cursor: grab;
          touch-action: none;
          display: flex;
        }

        p {
          font-weight: 300;
          font-size: 1rem;
          margin: auto auto;
          animation: gradient-blink 3s ease-in-out infinite;
          background: linear-gradient(
            90deg,
            #FFFFFF 0%,
            #FFFFFF 30%,
            #DC007D 50%,
            #FFFFFF 70%,
            #FFFFFF 100%
          );
          background-size: 350% 100%;
          background-clip: text;
          color: transparent;
        }

        @keyframes gradient-blink {
          0% {
            background-position: 100% 50%;
            filter: brightness(1);
          }
          70% {
            background-position: 0% 50%;
            filter: brightness(1.2);
          }
          100% {
            background-position: 0% 50%;
            filter: brightness(1);
          }
        }

        cup-icon {
          margin: auto auto;
        }
      </style>
      <div class="slider">
        <div class="handle">
          <cup-icon style="width: 32px; height: 32px;"></cup-icon>
        </div>
        <p>${this.prompt}</p>
      </div>
    `;
  }
}

customElements.define('slide-to-unlock', SlideToUnlock);
